import { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Wallet, Lock, Unlock, Plus, TrendingUp, TrendingDown, History, RotateCcw, X, Receipt } from "lucide-react";
import ReceiptModal from "@/components/ReceiptModal";

const money = (n) => `$${(Number(n) || 0).toFixed(2)}`;
const moveTypes = [["ingreso", "Ingreso"], ["egreso", "Egreso"], ["gasto", "Gasto"], ["devolucion", "Devolución"]];
const methods = [["cash", "Efectivo"], ["card", "Tarjeta"], ["transfer", "Transferencia"], ["other", "Otro"]];

export default function Caja() {
  const { can } = useAuth();
  const [summary, setSummary] = useState(undefined); // undefined=loading, null=closed
  const [detail, setDetail] = useState({ sales: [], movements: [] });
  const [history, setHistory] = useState([]);
  const [openForm, setOpenForm] = useState(null); // {opening_amount, note}
  const [closeForm, setCloseForm] = useState(null); // {counted_cash, note}
  const [moveForm, setMoveForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [receiptId, setReceiptId] = useState(null);

  const load = useCallback(async () => {
    try {
      const s = await api.get("/cash/summary");
      setSummary(s.data);
      if (s.data) {
        const d = await api.get("/cash/movements");
        setDetail({ sales: d.data.sales || [], movements: d.data.movements || [] });
      } else {
        setDetail({ sales: [], movements: [] });
      }
    } catch { setSummary(null); }
    if (can("cash:history")) api.get("/cash/history").then((r) => setHistory(r.data)).catch(() => {});
  }, [can]);
  useEffect(() => { load(); }, [load]);

  const doOpen = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      await api.post("/cash/open", { opening_amount: parseFloat(openForm.opening_amount || 0), note: openForm.note || "" });
      toast.success("Caja abierta"); setOpenForm(null); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const doMove = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      await api.post("/cash/movement", { type: moveForm.type, concept: moveForm.concept, method: moveForm.method,
        amount: parseFloat(moveForm.amount || 0), reference: moveForm.reference || "", note: moveForm.note || "" });
      toast.success("Movimiento registrado"); setMoveForm(null); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const doClose = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const r = await api.post("/cash/close", { counted_cash: parseFloat(closeForm.counted_cash || 0), note: closeForm.note || "" });
      const st = r.data.cuadre_state;
      toast.success(`Caja cerrada · ${st === "cuadre" ? "Cuadre exacto" : st === "sobrante" ? "Sobrante" : "Faltante"} ${money(r.data.difference)}`);
      setCloseForm(null); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const doReopen = async (id) => {
    if (!window.confirm("¿Reabrir esta caja?")) return;
    try { await api.post(`/cash/${id}/reopen`); toast.success("Caja reabierta"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const isOpen = !!summary;
  const stateColor = { cuadre: "text-[#39FF14]", sobrante: "text-[#FFD60A]", faltante: "text-[#FF3B30]" };

  return (
    <div data-testid="caja-page">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
        <div><p className="overline text-[#00f0ff]">Control de efectivo</p><h1 className="font-display text-5xl">CAJA DIARIA</h1></div>
        {summary === undefined ? null : isOpen ? (
          <div className="flex gap-2">
            {can("cash:movement") && <button data-testid="caja-movement-button" onClick={() => setMoveForm({ type: "ingreso", method: "cash", concept: "", amount: "", reference: "", note: "" })} className="flex items-center gap-2 border border-white/20 text-sm px-4 py-2.5 hover:bg-white/5"><Plus className="w-4 h-4" /> Movimiento</button>}
            {can("cash:close") && <button data-testid="caja-close-button" onClick={() => setCloseForm({ counted_cash: "", note: "" })} className="flex items-center gap-2 bg-[#FF3B30] text-white font-bold uppercase text-sm px-5 py-2.5 hover:bg-[#d92e25]"><Lock className="w-4 h-4" /> Cerrar caja</button>}
          </div>
        ) : (
          can("cash:open") && <button data-testid="caja-open-button" onClick={() => setOpenForm({ opening_amount: "", note: "" })} className="flex items-center gap-2 bg-[#00f0ff] text-black font-bold uppercase text-sm px-5 py-2.5 hover:bg-[#00c2cc]"><Unlock className="w-4 h-4" /> Abrir caja</button>
        )}
      </div>

      {summary === undefined && <p className="text-white/30">Cargando...</p>}

      {summary === null && (
        <div className="bg-[#0a0a0a] border border-white/10 p-12 text-center" data-testid="caja-closed-state">
          <Wallet className="w-12 h-12 mx-auto text-white/20 mb-4" />
          <p className="text-white/50">No hay caja abierta en esta sucursal.</p>
          <p className="text-white/30 text-sm mt-1">Abre la caja para comenzar a registrar cobros y movimientos.</p>
        </div>
      )}

      {isOpen && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6" data-testid="caja-summary">
            <Stat label="Fondo inicial" value={money(summary.opening_amount)} />
            <Stat label="Ventas efectivo" value={money(summary.sales_cash)} color="text-[#39FF14]" />
            <Stat label="Otros ingresos" value={money(summary.other_cash_income)} color="text-[#39FF14]" />
            <Stat label="Egresos" value={money(summary.egresos)} color="text-[#FF3B30]" />
            <Stat label="Ventas tarjeta" value={money(summary.sales_card)} />
            <Stat label="Ventas transferencia" value={money(summary.sales_transfer)} />
            <Stat label="Total ingresos" value={money(summary.total_income)} />
            <Stat label="Esperado en efectivo" value={money(summary.expected_cash)} color="text-[#00f0ff]" big />
          </div>
          <p className="text-xs text-white/40 mb-6">Caja abierta por <b className="text-white/70">{summary.register?.opened_by}</b> · {new Date(summary.register?.opened_at).toLocaleString()} · {summary.sales_count} ventas · {summary.movements_count} movimientos</p>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Panel title="Movimientos manuales" testid="caja-movements">
              {detail.movements.length === 0 && <Empty text="Sin movimientos." />}
              {detail.movements.map((m) => (
                <Row key={m.id}>
                  <div className="flex items-center gap-2">
                    {["egreso", "gasto", "devolucion"].includes(m.type) ? <TrendingDown className="w-4 h-4 text-[#FF3B30]" /> : <TrendingUp className="w-4 h-4 text-[#39FF14]" />}
                    <div><p className="text-sm">{m.concept || m.type}</p><p className="text-[11px] text-white/40 capitalize">{m.type} · {m.method}</p></div>
                  </div>
                  <span className={`font-mono-logs ${["egreso", "gasto", "devolucion"].includes(m.type) ? "text-[#FF3B30]" : "text-[#39FF14]"}`}>{money(m.amount)}</span>
                </Row>
              ))}
            </Panel>
            <Panel title="Ventas del turno" testid="caja-sales">
              {detail.sales.length === 0 && <Empty text="Sin ventas." />}
              {detail.sales.map((s) => (
                <Row key={s.id}>
                  <div><p className="text-sm">{s.type === "plan" ? s.plan_name : (s.items || []).map((i) => `${i.qty}x ${i.name}`).join(", ")}</p><p className="text-[11px] text-white/40 capitalize">{s.member_name} · {s.payment_method}</p></div>
                  <div className="flex items-center gap-2">
                    <span className="font-mono-logs text-[#39FF14]">{money(s.total)}</span>
                    {can("receipts:print") && <button data-testid={`caja-receipt-${s.id}`} onClick={() => setReceiptId(s.id)} className="p-1.5 hover:bg-white/10 text-[#00f0ff]" title="Ver / imprimir recibo"><Receipt className="w-4 h-4" /></button>}
                  </div>
                </Row>
              ))}
            </Panel>
          </div>
        </>
      )}

      {can("cash:history") && (
        <div className="mt-10">
          <p className="overline mb-3 flex items-center gap-2"><History className="w-4 h-4" /> Historial de cierres</p>
          <div className="bg-[#0a0a0a] border border-white/10 overflow-x-auto">
            <table className="w-full text-sm" data-testid="caja-history-table">
              <thead><tr className="text-left overline border-b border-white/10">
                <th className="px-4 py-3">Apertura</th><th className="px-4 py-3">Cierre</th><th className="px-4 py-3 text-right">Esperado</th>
                <th className="px-4 py-3 text-right">Contado</th><th className="px-4 py-3 text-right">Diferencia</th><th className="px-4 py-3">Estado</th><th className="px-4 py-3"></th>
              </tr></thead>
              <tbody>
                {history.length === 0 && <tr><td colSpan="7" className="px-4 py-8 text-center text-white/30">Sin cierres aún.</td></tr>}
                {history.map((h) => (
                  <tr key={h.id} className="border-b border-white/5 hover:bg-white/5">
                    <td className="px-4 py-3 text-xs font-mono-logs">{new Date(h.opened_at).toLocaleString()}</td>
                    <td className="px-4 py-3 text-xs font-mono-logs">{h.closed_at ? new Date(h.closed_at).toLocaleString() : "—"}</td>
                    <td className="px-4 py-3 text-right font-mono-logs">{money(h.summary?.expected_cash)}</td>
                    <td className="px-4 py-3 text-right font-mono-logs">{money(h.counted_cash)}</td>
                    <td className={`px-4 py-3 text-right font-mono-logs ${stateColor[h.cuadre_state] || ""}`}>{money(h.difference)}</td>
                    <td className="px-4 py-3"><span className={`text-xs uppercase font-bold ${stateColor[h.cuadre_state] || ""}`}>{h.cuadre_state}</span></td>
                    <td className="px-4 py-3 text-right">
                      {can("cash:reopen") && !isOpen && <button data-testid={`caja-reopen-${h.id}`} onClick={() => doReopen(h.id)} className="p-1.5 hover:bg-white/10 text-[#FFD60A]" title="Reabrir"><RotateCcw className="w-4 h-4" /></button>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {openForm && (
        <Modal title="ABRIR CAJA" onClose={() => setOpenForm(null)} onSubmit={doOpen} busy={busy} testid="caja-open-form">
          <Field label="Fondo inicial (efectivo) *"><input data-testid="caja-open-amount" required type="number" step="0.01" value={openForm.opening_amount} onChange={(e) => setOpenForm({ ...openForm, opening_amount: e.target.value })} className="inp" /></Field>
          <Field label="Nota"><input data-testid="caja-open-note" value={openForm.note} onChange={(e) => setOpenForm({ ...openForm, note: e.target.value })} className="inp" /></Field>
        </Modal>
      )}

      {moveForm && (
        <Modal title="MOVIMIENTO DE CAJA" onClose={() => setMoveForm(null)} onSubmit={doMove} busy={busy} testid="caja-move-form">
          <Field label="Tipo"><select data-testid="caja-move-type" value={moveForm.type} onChange={(e) => setMoveForm({ ...moveForm, type: e.target.value })} className="inp">{moveTypes.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></Field>
          <Field label="Método"><select data-testid="caja-move-method" value={moveForm.method} onChange={(e) => setMoveForm({ ...moveForm, method: e.target.value })} className="inp">{methods.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></Field>
          <Field label="Concepto *"><input data-testid="caja-move-concept" required value={moveForm.concept} onChange={(e) => setMoveForm({ ...moveForm, concept: e.target.value })} className="inp" /></Field>
          <Field label="Monto *"><input data-testid="caja-move-amount" required type="number" step="0.01" value={moveForm.amount} onChange={(e) => setMoveForm({ ...moveForm, amount: e.target.value })} className="inp" /></Field>
        </Modal>
      )}

      {closeForm && (
        <Modal title="CERRAR CAJA" onClose={() => setCloseForm(null)} onSubmit={doClose} busy={busy} testid="caja-close-form" danger>
          <div className="bg-[#121212] border border-white/10 p-3 mb-2 text-sm"><span className="text-white/50">Esperado en efectivo:</span> <b className="text-[#00f0ff]">{money(summary?.expected_cash)}</b></div>
          <Field label="Efectivo contado *"><input data-testid="caja-close-counted" required type="number" step="0.01" value={closeForm.counted_cash} onChange={(e) => setCloseForm({ ...closeForm, counted_cash: e.target.value })} className="inp" /></Field>
          {closeForm.counted_cash !== "" && <p className="text-sm">Diferencia: <b className={((parseFloat(closeForm.counted_cash) - summary.expected_cash) === 0) ? "text-[#39FF14]" : (parseFloat(closeForm.counted_cash) - summary.expected_cash) > 0 ? "text-[#FFD60A]" : "text-[#FF3B30]"}>{money(parseFloat(closeForm.counted_cash) - summary.expected_cash)}</b></p>}
          <Field label="Nota"><input data-testid="caja-close-note" value={closeForm.note} onChange={(e) => setCloseForm({ ...closeForm, note: e.target.value })} className="inp" /></Field>
        </Modal>
      )}

      <ReceiptModal saleId={receiptId} onClose={() => setReceiptId(null)} />
    </div>
  );
}

const Stat = ({ label, value, color = "text-white", big }) => (
  <div className={`bg-[#0a0a0a] border border-white/10 p-4 ${big ? "col-span-2 lg:col-span-1" : ""}`}>
    <p className="overline text-white/40">{label}</p>
    <p className={`font-display ${big ? "text-4xl" : "text-2xl"} ${color}`}>{value}</p>
  </div>
);
const Panel = ({ title, children, testid }) => (
  <div className="bg-[#0a0a0a] border border-white/10 p-4" data-testid={testid}>
    <p className="overline mb-3">{title}</p><div className="max-h-80 overflow-y-auto">{children}</div>
  </div>
);
const Row = ({ children }) => <div className="flex items-center justify-between py-2 border-b border-white/5">{children}</div>;
const Empty = ({ text }) => <p className="text-white/30 text-sm py-4">{text}</p>;
const Field = ({ label, children }) => <div><label className="overline block mb-1.5">{label}</label>{children}</div>;
const Modal = ({ title, children, onClose, onSubmit, busy, testid, danger }) => (
  <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/70 p-4 overflow-y-auto">
    <form onSubmit={onSubmit} data-testid={testid} className="bg-[#0a0a0a] border border-white/20 w-full max-w-md my-12">
      <div className="flex items-center justify-between px-6 py-4 border-b border-white/10"><h3 className="font-display text-2xl">{title}</h3><button type="button" onClick={onClose}><X className="w-5 h-5 text-white/50" /></button></div>
      <div className="p-6 space-y-4">{children}</div>
      <div className="flex justify-end gap-2 px-6 py-4 border-t border-white/10">
        <button type="button" onClick={onClose} className="border border-white/20 px-5 py-2.5 text-sm hover:bg-white/5">Cancelar</button>
        <button type="submit" disabled={busy} className={`${danger ? "bg-[#FF3B30] text-white hover:bg-[#d92e25]" : "bg-[#00f0ff] text-black hover:bg-[#00c2cc]"} font-bold uppercase text-sm px-6 py-2.5 disabled:opacity-50`}>{busy ? "..." : "Confirmar"}</button>
      </div>
    </form>
  </div>
);
